import {
  Badge, Button, Card, DataTable, DateInput, ErrorState, Field, Notice, PageHeader, Pager, Row, Stack, Textarea,
  useApi, useClampedPage, useI18n,
  type AbsenceNoticeResponse,
} from '@daghfal/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { MY_CHILDREN_KEY } from '../myChildren';

/**
 * Telling the nursery in advance that a child will be away (FR-ATT-11), and the attendance history
 * FR-ATT-13 asks for.
 *
 * A notified absence is counted apart from an unexplained one, and it reaches the class teacher rather
 * than sitting in a list nobody opens. That is the difference between this and a message: it changes what
 * the board says tomorrow morning.
 */
export function Absence({ childId }: { childId: number }) {
  const api = useApi();
  const { t, formatDate, formatNumber, today } = useI18n();
  const queryClient = useQueryClient();

  const [dateFrom, setDateFrom] = useState(today);
  const [dateTo, setDateTo] = useState('');
  const [reason, setReason] = useState('');

  const history = useQuery({
    queryKey: ['attendance', 'history', childId],
    queryFn: () => api.attendance.history(childId),
  });

  const notify = useMutation({
    mutationFn: () => api.attendance.notifyAbsence(childId, {
      dateFrom,
      dateTo: dateTo || null,
      reason: reason.trim() || null,
    }),
    onSuccess: async () => {
      setReason('');
      setDateTo('');
      await queryClient.invalidateQueries({ queryKey: ['attendance'] });
      await queryClient.invalidateQueries({ queryKey: MY_CHILDREN_KEY });
    },
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (dateFrom) notify.mutate();
  };

  return (
    <Stack>
      <PageHeader title={t('attendance.title')} />

      <Card title={t('attendance.notifyAbsence')}>
        <form onSubmit={submit}>
          <Stack>
            {notify.isError && <ErrorState error={notify.error} />}
            {notify.isSuccess && <Notice tone="success">{t('attendance.absenceSent')}</Notice>}

            <Row>
              <Field label={t('attendance.absenceFrom')} required>
                {(props) => (
                  <DateInput
                    {...props}
                    value={dateFrom}
                    onChange={setDateFrom}
                  />
                )}
              </Field>

              {/* Left empty, it is a single day — which is what most absences are. */}
              <Field label={t('attendance.absenceTo')}>
                {(props) => (
                  <DateInput
                    {...props}
                    value={dateTo}
                    min={dateFrom}
                    onChange={setDateTo}
                  />
                )}
              </Field>
            </Row>

            <Field label={t('attendance.absenceReason')}>
              {(props) => (
                <Textarea
                  {...props}
                  rows={2}
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                />
              )}
            </Field>

            <Row>
              <Button type="submit" busy={notify.isPending} busyLabel={t('action.saving')}>
                {t('attendance.notifyAbsence')}
              </Button>
            </Row>
          </Stack>
        </form>
      </Card>

      <NoticesSent childId={childId} />

      {history.data && (
        <Notice tone="info">
          {t('attendance.summary', {
            present: formatNumber(history.data.present),
            absent: formatNumber(history.data.absent),
            notified: formatNumber(history.data.notified),
          })}
        </Notice>
      )}

      <Card flush>
        <DataTable
          caption={t('attendance.title')}
          // Newest first: a parent checking this is looking at the last few days, not at September.
          rows={[...(history.data?.days ?? [])].reverse()}
          rowKey={(day) => day.date}
          isLoading={history.isPending}
          error={history.error}
          onRetry={() => void history.refetch()}
          empty={t('state.empty')}
          columns={[
            { key: 'date', header: t('audit.when'), render: (day) => formatDate(day.date) },
            {
              key: 'status',
              header: t('audit.action'),
              render: (day) => {
                switch (day.status) {
                  case 'Present':
                  case 'Departed':
                    return <Badge tone="success">{t('attendance.present')}</Badge>;
                  case 'Absent':
                    return <Badge tone="danger">{t('attendance.absent')}</Badge>;
                  case 'NotifiedAbsence':
                    return <Badge tone="warning">{t('attendance.notified')}</Badge>;
                  default:
                    // A weekend, a closure, or a day before the cut-off — none of which is an absence.
                    return <span className="dg-muted">—</span>;
                }
              },
            },
          ]}
        />
      </Card>
    </Stack>
  );
}

/**
 * The notices this family has given that have not ended yet, newest day last, and withdrawing one before its first day
 * (FR-ATT-11, GAPS 5.2). Once a notice has started the API keeps it, because it has already explained a morning on the
 * board; the row says so instead of offering a button the API would refuse (decision 2026-10-28 #7).
 */
function NoticesSent({ childId }: { childId: number }) {
  const api = useApi();
  const { t, formatDate, today } = useI18n();
  const day = today();
  const queryClient = useQueryClient();
  const [withdrawing, setWithdrawing] = useState<number | null>(null);
  const [page, setPage] = useState(1);

  // This child's from today, so a notice still running is listed with the ones to come, and with no end, so a notice
  // months ahead can be withdrawn too.
  const notices = useQuery({
    queryKey: ['attendance', 'notices', day, 'upcoming', childId, page],
    queryFn: () => api.attendance.notices({ from: day, upcoming: true, childId, page }),
  });
  useClampedPage(page, setPage, notices.data?.totalPages);

  const withdraw = useMutation({
    mutationFn: (id: number) => api.attendance.cancelNotice(id),
    onSuccess: async () => {
      setWithdrawing(null);
      await queryClient.invalidateQueries({ queryKey: ['attendance'] });
      await queryClient.invalidateQueries({ queryKey: MY_CHILDREN_KEY });
    },
  });

  const mine = notices.data?.items ?? [];
  const daysOf = (notice: AbsenceNoticeResponse) => (notice.dateFrom === notice.dateTo
    ? formatDate(notice.dateFrom)
    : t('calendar.range', { from: formatDate(notice.dateFrom), to: formatDate(notice.dateTo) }));

  return (
    <Card title={t('notices.title')}>
      <Stack>
        {notices.isError && <ErrorState error={notices.error} onRetry={() => void notices.refetch()} />}
        {withdraw.isError && <ErrorState error={withdraw.error} />}
        {notices.isSuccess && mine.length === 0 && <p className="dg-card__hint">{t('notices.none')}</p>}

        {mine.map((notice) => {
          const days = daysOf(notice);
          const started = notice.dateFrom <= day;
          return (
            <Stack key={notice.id} tight>
              <Row between>
                <span className="dg-stack dg-stack--tight">
                  <strong>{days}</strong>
                  {notice.reason && <span className="dg-card__hint">{notice.reason}</span>}
                </span>
                {!started && withdrawing !== notice.id && (
                  <Button
                    variant="ghost"
                    aria-label={t('notices.withdrawOf', { days })}
                    onClick={() => { withdraw.reset(); setWithdrawing(notice.id); }}
                  >
                    {t('notices.withdraw')}
                  </Button>
                )}
              </Row>
              {started && <span className="dg-card__hint">{t('notices.started')}</span>}
              {withdrawing === notice.id && (
                <>
                  <Notice tone="warning">{t('notices.withdrawHint')}</Notice>
                  <Row>
                    <Button variant="danger" busy={withdraw.isPending} onClick={() => withdraw.mutate(notice.id)}>
                      {t('notices.withdrawConfirm')}
                    </Button>
                    <Button variant="ghost" onClick={() => setWithdrawing(null)}>{t('action.cancel')}</Button>
                  </Row>
                </>
              )}
            </Stack>
          );
        })}
        <Pager page={page} totalPages={notices.data?.totalPages} onPage={setPage} />
      </Stack>
    </Card>
  );
}
